import React, { useState, useMemo, useRef } from 'react';

import { renderModal } from 'utils/modal_util';
import { useAppDispatch, useAppSelector } from 'utils/hooks';

import photoModule from 'domains/photos';

import CollectionsModal from 'components/features/ImageCollections/CollectionsModal';
import CollectionTile from './CollectionTile';

import DeleteCollectionModal from './DeleteCollectionModal';
import RenameCollectionModal from './RenameCollectionModal';
import CreateNewCollectionModal from './CreateNewCollectionModal';

import Button from 'components/shared/Button';

import type { CollectionName, PendingCollectionAction } from 'domains/photos';

const collectionFilterStorageKey = 'bp_collection_filter';

type SelectCollectionModalProps = {
  onClose: () => void;
  setDisplayCollection: (name: CollectionName) => void;
};

export function SelectCollectionModal({
  onClose,
  setDisplayCollection,
}: SelectCollectionModalProps) {
  const elem = useRef<HTMLUListElement>(null);

  const [filterValue, setFilterValue] = useState(
    sessionStorage.getItem(collectionFilterStorageKey) || ''
  );

  const dispatch = useAppDispatch();
  const namedCollections = useAppSelector(photoModule.selectors.allNamedCollections);
  const uncollected = useAppSelector(photoModule.selectors.uncollectedCollection);

  const [pendingEdit, setPendingEdit] = useState<CollectionName | undefined>(undefined);
  const [pendingDelete, setPendingDelete] = useState<string | undefined>(undefined);
  const [pendingCreate, setPendingCreate] = useState(false);

  const pendingEditCollection = useMemo(
    () => [...namedCollections, uncollected].find((c) => c.name === pendingEdit),
    [pendingEdit, namedCollections, uncollected]
  );

  const pendingDeleteCollection = useMemo(
    () => namedCollections.find((c) => c.name === pendingDelete),
    [pendingDelete, namedCollections]
  );

  const collections = useMemo(() => {
    return uncollected.count ? [uncollected, ...namedCollections] : namedCollections;
  }, [uncollected, namedCollections]);

  // Sort uncollected first, then all others alphabetically
  const sortedCollections = useMemo(() => {
    return collections
      .filter((c) => c.count)
      .filter((c) => (c.name || 'Uncollected').indexOf(filterValue) > -1)
      .sort((a, b) => {
        if (a.name === null) return -1;
        if (b.name === null) return 1;
        return a.name > b.name ? 1 : -1;
      });
  }, [collections, filterValue]);

  function handleCollectionFilter(value: string) {
    setFilterValue(value);

    if (value === '') {
      sessionStorage.removeItem(collectionFilterStorageKey);
    } else {
      sessionStorage.setItem(collectionFilterStorageKey, value);
    }
  }

  const createNewButton = (
    <Button
      type="outline"
      id="create-new-collection"
      className="create-new-collection"
      onClick={() => setPendingCreate(true)}
    >
      Create New Collection
    </Button>
  );

  function handleSelectCollection(collection: CollectionName) {
    dispatch(photoModule.actions.setActiveCollection(collection));
    onClose();
  }

  function handleDeleteCollection(collection: string) {
    dispatch(photoModule.actions.removeAllForCollection(collection));
    setPendingDelete(undefined);
  }

  function handleEditCollection(current: CollectionName, change: string) {
    dispatch(photoModule.actions.renameCollectionPhotos.pending({ current, change }));
    setPendingEdit(undefined);
  }

  function handleCreateCollection(
    name: string,
    action: PendingCollectionAction,
    donorIds: number[]
  ) {
    dispatch(photoModule.actions.createNewCollection({ name, action, donorIds }));
    onClose();
  }

  return (
    <React.Fragment>
      <CollectionsModal
        title="Your Image Collections"
        buttons={createNewButton}
        lastFocusedElementId="collections-menu-trigger"
        onClose={onClose}
        filter={{
          placeholder: 'Find Collections',
          onChange: handleCollectionFilter,
          value: filterValue,
        }}
      >
        {namedCollections.length === 0 ? (
          <p>You have no image collections. Start by creating one now.</p>
        ) : (
          <ul
            className="collections_manager_items modal-list-items collections animated fadeIn"
            ref={elem}
          >
            {sortedCollections.map((collection) => (
              <li key={`${collection.name}${collection.count}`}>
                <CollectionTile
                  collection={collection}
                  onOpen={setDisplayCollection}
                  onSelect={handleSelectCollection}
                  onEdit={setPendingEdit}
                  onDelete={setPendingDelete}
                />
              </li>
            ))}
          </ul>
        )}
      </CollectionsModal>

      {pendingCreate &&
        renderModal(
          <CreateNewCollectionModal
            uncollected={uncollected}
            collections={namedCollections}
            onCancel={() => setPendingCreate(false)}
            onConfirm={handleCreateCollection}
          />
        )}

      {pendingEditCollection &&
        renderModal(
          <RenameCollectionModal
            collections={namedCollections}
            current={pendingEditCollection}
            onCancel={() => setPendingEdit(undefined)}
            onConfirm={handleEditCollection}
          />
        )}

      {pendingDeleteCollection &&
        renderModal(
          <DeleteCollectionModal
            pendingDelete={pendingDeleteCollection}
            onCancel={() => setPendingDelete(undefined)}
            onConfirm={handleDeleteCollection}
          />
        )}
    </React.Fragment>
  );
}
